/**
 * SelectionSummaryHelper
 * 
 * Static helper methods for building and formatting selection summaries.
 * 
 * Methods:
 * 1. transformData() - Transform data list into formatted key-value strings
 * 2. replacePipe() - Replace pipe characters with colon-space separator
 * 3. buildSelectionSummaryText() - Build selection summary text with all sections
 * 4. parseSelectionSummary() - Parse selection summary text into sections
 * 5. replacePipeForMcode() - Strip trailing |Yes / |No marker, then replace pipes
 * 6. SelectionSummary() - Read the dataObject selections and rebuild the summary
 * 7. BindSelectionSummary() - Bind formatted selections + ribbon state to the summary
 * 8. GenerateSelectionSummary() - Parse the stored summary into displayable sections
 * 9. buildSnapshotSelectionSummary() - Build the Snapshot (mcode 1) summary string
 * 10. SelectionsummaryCollapseExpand() - Open the summary panel + build the chip lists
 * 11. OnCloseClick() - Close the summary panel
 */

/** Mirrors the non-exported BASE_TYPES const in chatbotexpand.component.ts. */
const BASE_TYPES = {
  TOTAL: 'Total Base',
  ANSWER: 'Answer Base'
} as const;

export class SelectionSummaryHelper {
  
  /**
   * EXACT COPY from chatbotexpand.component.ts (lines 2545-2592)
   * Transform data list into formatted key-value strings
   */
  static transformData(dataList: string[]): string[] {
    const transformedData: { [key: string]: { [subKey: string]: string[] } } =
      {};

    dataList.forEach((item) => {
      const parts = item.split('|');
      const mainKey = parts[0];

      if (parts.length > 1) {
        const subKey = parts.slice(1, -1).join('|');
        const value = parts.slice(-1)[0];

        if (!transformedData[mainKey]) {
          transformedData[mainKey] = {};
        }

        if (!transformedData[mainKey][subKey]) {
          transformedData[mainKey][subKey] = [];
        }

        transformedData[mainKey][subKey].push(value);
      } else {
        // Handle single-value case
        if (!transformedData[mainKey]) {
          transformedData[mainKey] = { '': [] };
        }

        transformedData[mainKey][''].push(parts[0]);
      }
    });

    return Object.keys(transformedData).map((mainKey) => {
      const subKeys = Object.keys(transformedData[mainKey]);
      const subStrings = subKeys.map((subKey) => {
        const subString =
          subKey === ''
            ? transformedData[mainKey][subKey]
            : `${subKey}: ${transformedData[mainKey][subKey].join(',')}`;
        return subString;
      });
      return `${mainKey}: ${subStrings.join(',').replace(/,/g, ' , ')}`;
    });
  }

  /**
   * EXACT COPY from chatbotexpand.component.ts (lines 1631-1633)
   * Replace pipe characters with colon-space separator
   */
  static replacePipe(input: string): string {
    return input.replace(/\|/g, ' : ');
  }

  /**
   * EXACT COPY from chatbotexpand.component.ts (lines 2462-2542)
   * Build selection summary text with all sections
   */
  static buildSelectionSummaryText(
    RowValues: string[],
    RowNestingValues: string[],
    ColumnValues: string[],
    ColumnNestingValues: string[],
    FilterData: string[],
    isSavedSelection: boolean,
    config: {
      selectedDataFormat: string;
      DefaultSampleSize: string;
      SignificanceVsReference: string;
      DefaultConfidenceLevel: string;
      selectedIndex: string;
      isIndex: boolean;
      DefaultWeight: string;
      isTotalBase: boolean;
      selected_study: string;
      isFilteringActive: boolean;
      BASE_TYPES: { TOTAL: string; ANSWER: string };
    }
  ): string {
    let rowNestingSection = '';
    let colNestSection = '';
    let FiltersSection = '';
    let significanceSection = '';
    let confidenceSection = '';
    let Reference = '';
    let Weight = '';
    let Base = '';
    let study = '';
    
    if (RowNestingValues.length > 0) {
      rowNestingSection = ` <span class="grey-separator"> || </span> <span class="summary-label">Row Nesting:</span> ${RowNestingValues.join(
        '&nbsp;,&nbsp;'
      )}`;
    }
    if (ColumnNestingValues.length > 0) {
      colNestSection = ` <span class="grey-separator"> || </span> <span class="summary-label">Column Nesting:</span> ${ColumnNestingValues.join(
        '&nbsp;,&nbsp;'
      )}`;
    }
    if (FilterData.length > 0) {
      FiltersSection = `  <span class="summary-label">Filters:</span> ${FilterData.join(
        '&nbsp;,&nbsp;'
      )} <span class="grey-separator"> || </span> `;
    }
    if (!isSavedSelection && config.selectedDataFormat == 'Respondent Level') {
      if (config.SignificanceVsReference != '') {
        significanceSection = `  <span class="summary-label">Significance:</span> ${config.DefaultSampleSize
          } : ${SelectionSummaryHelper.replacePipe(
            config.SignificanceVsReference
          )} <span class="grey-separator"> || </span> `;
      } else {
        significanceSection = `  <span class="summary-label">Significance:</span> ${config.DefaultSampleSize} <span class="grey-separator"> || </span> `;
      }
      confidenceSection = `  <span class="summary-label">Confidence Level:</span> ${config.DefaultConfidenceLevel} <span class="grey-separator"> || </span> `;
    }
    if (
      config.selectedIndex != '' &&
      config.isIndex &&
      config.selectedDataFormat == 'Respondent Level'
    ) {
      Reference = `  <span class="summary-label">Reference:</span> ${SelectionSummaryHelper.replacePipe(
        config.selectedIndex
      )} <span class="grey-separator"> || </span> `;
    }
    if (
      config.DefaultWeight != '' &&
      config.selectedDataFormat == 'Respondent Level'
    ) {
      Weight = `  <span class="summary-label">Calculation Weight:</span> Primary Weight <span class="grey-separator"> || </span> `;
    }
    if (config.selectedDataFormat == 'Respondent Level') {
      Base = `<span class="summary-label">Calculation Base:</span> ${config.isTotalBase ? config.BASE_TYPES.TOTAL : config.BASE_TYPES.ANSWER
        } <span class="grey-separator"> || </span> `;
    }
    study = `<span class="summary-label">Study Type:</span> ${config.selected_study} <span class="grey-separator"> || </span> `;

    let selectionSummary = `${study}  ${Base} <span class="summary-label">Nesting Type:</span> ${config.isFilteringActive ? 'Filtering' : 'Profiling'
      } <span class="grey-separator"> || </span> ${Weight} ${significanceSection} ${confidenceSection} ${Reference}  ${FiltersSection} <span class="summary-label">Row:</span> ${RowValues.join(
        '&nbsp;,&nbsp;'
      )}
    ${rowNestingSection} <span class="grey-separator"> || </span> <span class="summary-label">Column:</span> ${ColumnValues.join(
        '&nbsp;,&nbsp;'
      )} ${colNestSection}`;

    selectionSummary = selectionSummary
      .replaceAll('@', ' , ')
      .replace(/<[^>]*>/g, '')           // Remove all HTML tags
      .replace(/&nbsp;/g, ' ')          // Replace &nbsp; with regular spaces
      .replace(/\s+/g, ' ')             // Replace multiple spaces with single space
      .replace(/\s*\|\|\s*/g, ' || ')   // Clean spacing around || separators
      .replace(/^\s*\|\|\s*/, '')       // Remove leading separator
      .replace(/\s*\|\|\s*$/, '')       // Remove trailing separator
      .trim();                          // Remove leading/trailing whitespace

    return selectionSummary;
  }

  /**
   * EXACT COPY from chatbotexpand.component.ts (lines 6771-6850)
   * Parse selection summary text from config service into sections
   */
  static parseSelectionSummary(
    selectionSummaryText: string
  ): {
    titleSection: { key: string; value: string }[];
    entryPoints: { key: string; value: string }[];
    selections: { key: string; value: string }[];
  } {
    const titleSection: { key: string; value: string }[] = [];
    const entryPoints: { key: string; value: string }[] = [];
    const selections: { key: string; value: string }[] = [];
    
    let selectionSummaryArray = selectionSummaryText.split("||");

    for (let i = 0; i < selectionSummaryArray.length; i++) {
      let section = selectionSummaryArray[i].trim();

      if (section.startsWith("Compare Across:")) {
        const titleContent = section.replace("Compare Across:", "").trim();
        const titleParts = titleContent.split(":");
        if (titleParts.length > 1) {
          titleSection.push({
            key: titleParts[0].trim(),
            value: titleParts.slice(1).join(":").trim()
          });
        } else {
          titleSection.push({
            key: "",
            value: titleContent
          });
        }
      }
      else if (section.startsWith("Entry Point:")) {
        const entryPointContent = section.replace("Entry Point:", "").trim();
        const entryPointItems = entryPointContent.split(";");

        for (let item of entryPointItems) {
          item = item.trim();
          if (item) {
            const itemParts = item.split(":");
            if (itemParts.length > 1) {
              entryPoints.push({
                key: itemParts[0].trim(),
                value: itemParts.slice(1).join(":").trim()
              });
            }
          }
        }
      }
      else if (section.startsWith("Selections:")) {
        let selectionsText = section.replace("Selections:", "").trim();
        let selectionItems = selectionsText.split(";");

        for (let item of selectionItems) {
          item = item.trim();
          if (item) {
            let itemParts = item.split(":");
            if (itemParts.length > 1) {
              let key = itemParts[0].trim();
              let value = itemParts.slice(1).join(":").trim();

              selections.push({
                key: key,
                value: value
              });
            } else {
              selections.push({
                key: '',
                value: item
              });
            }
          }
        }
      }
      
      else if (section.startsWith("Filters:")) {
        const filtersContent = section.replace("Filters:", "").trim();
        const filterItems = filtersContent.split(" | ");
        filterItems.forEach((item) => {
          item = item.trim();
          if (item) {
            selections.push({
              key: "Filters",
              value: item
            });
          }
        });
      }

      else if (section) {
        let sectionParts = section.split(":");
        if (sectionParts.length > 1) {
          let key = sectionParts[0].trim();
          let value = sectionParts.slice(1).join(":").trim();

          selections.push({
            key: key,
            value: value
          });
        }
      }
    }

    return {
      titleSection,
      entryPoints,
      selections
    };
  }

  /**
   * Strips the trailing "|Yes" / "|No" marker that Brand Analysis and Crosstab
   * (mcode 2 / 3) append to a reference selection, then formats the remaining pipes.
   */
  static replacePipeForMcode(comp: any, input: string): string {
    let cleaned = input;
    if ((comp.mcode == 2 || comp.mcode == 3) && typeof cleaned === 'string') {
      if (cleaned.endsWith('|Yes')) cleaned = cleaned.substring(0, cleaned.length - 4);
      else if (cleaned.endsWith('|No')) cleaned = cleaned.substring(0, cleaned.length - 3);
    }
    return SelectionSummaryHelper.replacePipe(cleaned);
  }

  /**
   * Collects the distinct Row / RowNesting / Column / ColNesting / Filter selections
   * off the live dataObject, formats them, and writes the resulting summary onto the
   * component.
   */
  static SelectionSummary(comp: any) {
    const disctinctRowValues = comp.getDistinctChildValues(comp.dataObject.Row);
    const disctinctRowNestingValues = comp.getDistinctChildValues(
      comp.dataObject.RowNesting
    );
    const disctinctColumnValues = comp.getDistinctChildValues(
      comp.dataObject.Column
    );
    const disctinctColumnNestingValues = comp.getDistinctChildValues(
      comp.dataObject.ColNesting
    );
    const FilterValues = comp.getDistinctChildValues(comp.dataObject.Filter);

    const RowValues = comp.transformData(disctinctRowValues);
    const RowNestingValues = comp.transformData(disctinctRowNestingValues);
    const ColumnValues = comp.transformData(disctinctColumnValues);
    const ColumnNestingValues = comp.transformData(
      disctinctColumnNestingValues
    );
    const FilterData = comp.transformData(FilterValues);
    comp.BindSelectionSummary(
      RowValues,
      RowNestingValues,
      ColumnValues,
      ColumnNestingValues,
      FilterData,
      false
    );
  }

  /**
   * Feeds the formatted selection arrays plus the current ribbon state into
   * buildSelectionSummaryText and stores the result on the component.
   */
  static BindSelectionSummary(
    comp: any,
    RowValues: string[],
    RowNestingValues: string[],
    ColumnValues: string[],
    ColumnNestingValues: string[],
    FilterData: string[],
    isSavedSelection: boolean
  ) {
    comp.selectionSummary = SelectionSummaryHelper.buildSelectionSummaryText(
      RowValues,
      RowNestingValues,
      ColumnValues,
      ColumnNestingValues,
      FilterData,
      isSavedSelection,
      {
        selectedDataFormat: comp.selectedDataFormat,
        DefaultSampleSize: comp.DefaultSampleSize,
        SignificanceVsReference: comp.SignificanceVsReference,
        DefaultConfidenceLevel: comp.DefaultConfidenceLevel,
        selectedIndex: comp.selectedIndex,
        isIndex: comp.isIndex,
        DefaultWeight: comp.DefaultWeight,
        isTotalBase: comp.isTotalBase,
        selected_study: comp.selected_study,
        isFilteringActive: comp.isFilteringActive,
        BASE_TYPES: BASE_TYPES
      }
    );
  }

  /**
   * Splits the stored summary string into the title / entry point / selection
   * chips rendered by the selection summary panel.
   */
  static GenerateSelectionSummary(comp: any, customSummary?: string) {
    const summaryToUse = customSummary || comp._configService.selectionSummaryTextTitle;
    const result = SelectionSummaryHelper.parseSelectionSummary(
      summaryToUse
    );
    comp.titleSection = result.titleSection;
    comp.entryPoints = result.entryPoints;
    comp.selections = result.selections;
    comp.selectionSummary = summaryToUse;
  }

  /**
   * Builds the Snapshot (mcode 1) selection summary string straight from the prompt
   * data, since snapshots have no left-panel dataObject to derive it from.
   */
  static buildSnapshotSelectionSummary(comp: any, data: any): string {
    const pData = data.promptData || comp.promptData || {};

    const extractVal = (item: any) => {
      if (typeof item === 'string') {
        const parts = item.split('|');
        if (parts.length >= 2) return parts[parts.length - 2] + ': ' + parts[parts.length - 1];
        return parts.pop();
      }
      if (item && item.name) return item.name;
      if (item && item.Child) return item.Child;
      return String(item);
    };

    const extractLastPart = (item: any) => {
      if (typeof item === 'string') return item.split('|').pop();
      if (item && item.name) return item.name;
      if (item && item.Child) return item.Child;
      return String(item);
    };

    const compareAcross = pData.Snapshot_Type || pData.snapshot_type || 'Single Brand';
    const section = pData.Section || pData.section || 'Analysis';

    const mktData = pData.Markets || pData.markets || pData.Market || pData.market || [];
    const tpData = pData.Time_Period || pData.time_period || pData.TimePeriod || pData.timeperiod || [];
    const brandsData = pData.Primary_Brands || pData.primary_brands || pData.Brands || pData.brands || [];
    const filtersData = pData.Filter_Audience || pData.filter_audience || pData.Filters || pData.filters || [];

    const markets = mktData.length > 0 ? mktData.map(extractLastPart).join(', ') : 'All Markets';
    const timeperiod = tpData.length > 0 ? tpData.map(extractVal).join(', ') : 'All Time Periods';
    const brands = brandsData.length > 0 ? brandsData.map(extractVal).join(', ') : 'All Brands';
    const filters = filtersData.length > 0 ? filtersData.map(extractVal).join(', ') : 'All Respondents';
    const studyInfo = comp.selected_study || '';

    const baseType = pData.isTotalBase ? BASE_TYPES.TOTAL : BASE_TYPES.ANSWER;

    let selections = 'Time Period: ' + timeperiod + '; Brands: ' + brands + '; Study: ' + studyInfo;
    if (filtersData.length > 0) {
      selections += '; Filters: ' + filters;
    }

    return 'Compare Across: ' + compareAcross + ': ' + section +
      ' || Entry Point: Markets: ' + markets +
      ' || Selections: ' + selections +
      ' || Significance: None' +
      ' || Base: ' + baseType +
      ' || Weight: Primary Weight';
  }

  /**
   * Opens the Selection Summary / Save Selections side panel and populates the
   * Rowselections / Columnselections / AllSelections chip lists for the given block.
   */
  static SelectionsummaryCollapseExpand(comp: any, text: any, block: any = null) {
    const target = block || comp;
    comp.isselectionsummarycollapse = true;
    setTimeout(() => {
      comp.isselectionsummarycollapse2 = true;
    }, 300);
    if (text == 'SelectionSummary') {
      comp.isselectionHeader = "Selection Summary";
      comp.issavedselectionblock = false;
    }
    else {
      comp.isselectionHeader = "Save Selections";
      comp.issavedselectionblock = true;
      comp.SaveSelectiontext = '';
    }

    // Determine which data object to use for summary
    const sourceDataObject = block ? comp.getOutputData(block) : comp.dataObject;
    comp.isSummaryForSnapshot = comp.showSnapshot || comp.isSnapshotVisible(target);

    if (comp.isSummaryForSnapshot) {
      comp.GenerateSelectionSummary(target.selectionSummary)
    }
    else {
      const activeMcode = block?.activeView?.mcode ?? block?.mcode ?? comp.mcode;
      let livePayload: any = null;
      if (activeMcode === 1) {
        livePayload = block?.snapshotPayload || comp.lastSnapshotPayload;
      } else if (activeMcode === 2) {
        livePayload = block?.brandAnalysisPayload || comp.lastBrandAnalysisPayload;
      } else if (activeMcode === 3) {
        livePayload = block?.crosstabPayload || comp.lastCrosstabPayload;
      }

      const toStructured = (arr: any): any[] => {
        if (!Array.isArray(arr) || arr.length === 0) return [];
        if (typeof arr[0] === 'string') {
          return comp.transformToDataObject(arr, 'Row');
        }
        return arr;
      };

      let effectiveRow: any[] = [];
      let effectiveColumn: any[] = [];
      let effectiveRowNesting: any[] = [];
      let effectiveColNesting: any[] = [];
      let structuredFilter: any[] | null = null;
      let rawFilters: any = null;

      if (livePayload) {
        effectiveRow = livePayload.Row || [];
        effectiveColumn = livePayload.Column || [];
        effectiveRowNesting = livePayload.RowNesting || [];
        effectiveColNesting = livePayload.ColNesting || [];
        structuredFilter = livePayload.Filter?.length > 0 ? livePayload.Filter : null;
        if (!structuredFilter) {
          rawFilters = livePayload.Filters || sourceDataObject?.Filters;
        }
      } else {
        const mr = block?.analyticalData?.member_responses;
        const viewKey = activeMcode === 1 ? 'snapshot' : (activeMcode === 2 ? 'brand_analysis' : 'crosstab');
        const viewData = mr ? (mr[viewKey] || mr[viewKey.replace('_', '')]) : null;

        if (viewData) {
          effectiveRow = toStructured(viewData.Row || []);
          effectiveColumn = toStructured(viewData.Column || []);
          effectiveRowNesting = toStructured(viewData.Row_Nesting || viewData.RowNesting || []);
          effectiveColNesting = toStructured(viewData.Column_Nesting || viewData.ColNesting || []);
          rawFilters = viewData.Filters || viewData.filters;
        } else {
          effectiveRow = sourceDataObject?.Row || [];
          effectiveColumn = sourceDataObject?.Column || [];
          effectiveRowNesting = sourceDataObject?.RowNesting || [];
          effectiveColNesting = sourceDataObject?.ColNesting || [];
          rawFilters = (block?.result?.Filters) || sourceDataObject?.Filters;
        }
      }

      const disctinctRowValues = comp.getDistinctChildValues(effectiveRow);
      const disctinctRowNestingValues = comp.getDistinctChildValues(effectiveRowNesting);
      const disctinctColumnValues = comp.getDistinctChildValues(effectiveColumn);
      const disctinctColumnNestingValues = comp.getDistinctChildValues(effectiveColNesting);
      let FilterValues: string[] = [];

      if (structuredFilter) {
        FilterValues = comp.getDistinctChildValues(structuredFilter);
      } else if (rawFilters && typeof rawFilters === 'object') {
        const flattenedFilters: string[] = [];
        Object.values(rawFilters).forEach((val: any) => {
          if (Array.isArray(val)) {
            flattenedFilters.push(...val.filter(Boolean));
          } else if (typeof val === 'string' && val.trim()) {
            flattenedFilters.push(val);
          }
        });
        FilterValues = flattenedFilters;
      } else {
        FilterValues = comp.getDistinctChildValues(sourceDataObject?.Filter || []);
      }

      const RowValues = comp.transformData(disctinctRowValues);
      const RowNestingValues = comp.transformData(disctinctRowNestingValues);
      const ColumnValues = comp.transformData(disctinctColumnValues);
      const ColumnNestingValues = comp.transformData(disctinctColumnNestingValues);
      const FilterData = comp.transformData(FilterValues);

      let isSavedSelection = false
      comp.Columnselections = [];
      comp.Rowselections = [];
      comp.AllSelections = [];

      comp.Columnselections = ColumnValues.map((item: string) => {
        const [key, ...valueParts] = item.split(':');
        return {
          key: key.trim(),
          value: valueParts.join(':').trim()
        };
      });

      comp.Rowselections = RowValues.map((item: string) => {
        const [key, ...valueParts] = item.split(':');
        return {
          key: key.trim(),
          value: valueParts.join(':').trim()
        };
      });

      if (FilterData.length > 0) {
        comp.AllSelections = FilterData.map((item: string) => {
          const [key, ...valueParts] = item.split(':');
          return {
            key: key.trim(),
            value: valueParts.join(':').trim()
          };
        });
      }

      if (RowNestingValues.length > 0) {
        let rowNestingSection = RowNestingValues.join(
          '&nbsp;,&nbsp;'
        );
        comp.AllSelections.push({
          key: 'Row Nesting',
          value: rowNestingSection
        });
      }
      if (ColumnNestingValues.length > 0) {
        let colNestSection = ColumnNestingValues.join(
          '&nbsp;,&nbsp;'
        );
        comp.AllSelections.push({
          key: 'Column Nesting',
          value: colNestSection
        });
      }

      if (!isSavedSelection && comp.selectedDataFormat == 'Respondent Level') {
        let significanceSection;
        if (comp.SignificanceVsReference != '') {
          significanceSection = `${comp.DefaultSampleSize
            } : ${comp.replacePipe(
              comp.SignificanceVsReference
            )}`;
        } else {
          significanceSection = comp.DefaultSampleSize;
        }
        comp.AllSelections.push({
          key: 'Significance',
          value: significanceSection
        });

        comp.AllSelections.push({
          key: 'Confidence Level',
          value: comp.DefaultConfidenceLevel
        });
      }

      if (
        comp.selectedIndex != '' &&
        comp.isIndex &&
        comp.selectedDataFormat == 'Respondent Level'
      ) {
        let Reference = `${comp.replacePipe(
          comp.selectedIndex
        )}`;

        comp.AllSelections.push({
          key: 'Reference',
          value: Reference
        });
      }
      if (
        target.DefaultWeight != '' &&
        comp.selectedDataFormat == 'Respondent Level'
      ) {
        comp.AllSelections.push({
          key: 'Calculation Weight',
          value: target.DefaultWeight
        });
      }
      if (comp.selectedDataFormat == 'Respondent Level') {
        let Base = `${target.isTotalBase ? BASE_TYPES.TOTAL : BASE_TYPES.ANSWER}`;
        comp.AllSelections.push({
          key: 'Calculation Base',
          value: Base
        });
      }
    }

  }

  /**
   * Closes the Selection Summary / Save Selections side panel.
   */
  static OnCloseClick(comp: any) {
    setTimeout(() => {
      comp.isselectionsummarycollapse = false;
    }, 500);
    comp.isselectionsummarycollapse2 = false;
    comp.issavedselectionblock = false;
  }
}
